import React, { useState, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useProjects } from '../../hooks/useProjects';
import { usePermissions } from '../../hooks/usePermissions';

const Sidebar = ({ mobile }) => {
  const location = useLocation();
  const { logout, currentUser } = useAuth();
  const { projectId } = useParams();
  const [selectedProject, setSelectedProject] = useState(projectId);
  const { useAllProjects } = useProjects();
  const { data: projectsData } = useAllProjects();
  const projects = projectsData || [];
  const permissions = usePermissions();

  // Update selected project when URL param changes
  useEffect(() => {
    if (projectId) {
      setSelectedProject(projectId);
    }
  }, [projectId]);

  // Main navigation items
  const mainNavigation = [
    { name: 'Dashboard', href: '/dashboard', emoji: '📊' },
    { name: 'Projects', href: '/projects', emoji: '🤖' },
  ];

  // Team management navigation (only for main users)
  const teamNavigation = [
    { name: 'Sub Users', href: '/sub-users', emoji: '👥' },
  ];

  // Project-specific navigation items (filtered based on permissions)
  const getProjectNavigation = () => {
    const navItems = [
      { name: 'Project Overview', href: `/projects/${selectedProject}`, emoji: '📋', check: () => permissions.canView('dashboard') },
      { name: 'API Manager', href: `/projects/${selectedProject}/api-manager`, emoji: '🔌', check: () => permissions.canView('apiEndpoints') },
      { name: 'Flow Builder', href: `/projects/${selectedProject}/flow-builder`, emoji: '🌊', check: () => permissions.canView('flowBuilder') },
      { name: 'Prompt Builder', href: `/projects/${selectedProject}/prompt-builder`, emoji: '✏️', check: () => permissions.canManage('project') },
      { name: 'Knowledge Base', href: `/projects/${selectedProject}/knowledge-base`, emoji: '📚', check: () => permissions.canView('knowledgeBase') },
      { name: 'Location Manager', href: `/projects/${selectedProject}/locations`, emoji: '📍', check: () => permissions.canView('locations') },
      { name: 'Chatbot Simulator', href: `/projects/${selectedProject}/simulator`, emoji: '💬', check: () => permissions.canTestChatbot },
      { name: 'Human Agents', href: `/projects/${selectedProject}/human-agents`, emoji: '👨‍💼', check: () => permissions.canView('humanAgents') },
      { name: 'SDK Integration', href: `/projects/${selectedProject}/sdk`, emoji: '🧩', check: () => permissions.canView('sdkIntegration') },
      { name: 'Analytics', href: `/projects/${selectedProject}/analytics`, emoji: '📈', check: () => permissions.canView('analytics') },
      { name: 'Billing & Costs', href: `/projects/${selectedProject}/billing`, emoji: '💰', check: () => permissions.canManage('project') },
    ];

    // Filter based on permissions
    return navItems.filter(item => item.check());
  };

  const projectNavigation = getProjectNavigation();

  // Determine if we're on a project-specific page
  const isProjectPage = location.pathname.includes('/projects/') && location.pathname !== '/projects';

  // Handle project change
  const handleProjectChange = (e) => {
    const newProjectId = e.target.value;
    setSelectedProject(newProjectId);
    
    // Redirect to the project overview page for the selected project
    if (newProjectId && location.pathname.includes('/projects/')) {
      const currentPath = location.pathname;
      const pathParts = currentPath.split('/');
      pathParts[2] = newProjectId; // Replace the project ID in the path
      window.location.href = pathParts.join('/');
    } else if (newProjectId) {
      window.location.href = `/projects/${newProjectId}`;
    }
  };

  return (
    <aside className={`app-sidebar ${mobile ? 'mobile' : 'desktop'}`}>
      <div className="sidebar-header">
        <h1 className="sidebar-title">NeoChat</h1>
      </div>
      
      <nav className="sidebar-nav">
        <ul className="nav-list">
          {mainNavigation.map((item) => (
            <li key={item.name} className="nav-item">
              <Link
                to={item.href}
                className={`nav-link ${location.pathname === item.href ? 'active' : ''}`}
              >
                <span className="nav-icon">{item.emoji}</span>
                {item.name}
              </Link>
            </li>
          ))}
        </ul>

        {/* Team Management - Only for main users */}
        {permissions.isMainUser && (
          <div className="mt-4">
            <h2 className="px-4 text-sm font-medium text-gray-500 mb-2">Team</h2>
            <ul className="nav-list">
              {teamNavigation.map((item) => (
                <li key={item.name} className="nav-item">
                  <Link
                    to={item.href}
                    className={`nav-link ${location.pathname === item.href ? 'active' : ''}`}
                  >
                    <span className="nav-icon">{item.emoji}</span>
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {isProjectPage && (
          <>
            <div className="mt-6 mb-2 px-4">
              <label htmlFor="project-selector" className="selector-label">
                Current Project:
              </label>
              <select
                id="project-selector"
                className="selector-input mt-1"
                value={selectedProject || ''}
                onChange={handleProjectChange}
              >
                <option value="" disabled>Select a project</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-4">
              <h2 className="px-4 text-sm font-medium text-gray-500 mb-2">Project Settings</h2>
              <ul className="nav-list">
                {projectNavigation.map((item) => (
                  <li key={item.name} className="nav-item">
                    <Link
                      to={item.href}
                      className={`nav-link ${location.pathname === item.href ? 'active' : ''}`}
                    >
                      <span className="nav-icon">{item.emoji}</span>
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </nav>
      
      <div className="sidebar-footer">
        <div className="user-info">
          <div className="user-details">
            <div className="user-name text-smd">{currentUser?.name || 'User'}</div>
            <div className="user-email text-smd mt-2">{currentUser?.email || 'user@example.com'}</div>
          </div>
        </div>
        <button onClick={logout} className="logout-button text-smd mt-2">
          <span className="nav-icon">🚪</span>
          Logout
        </button>
      </div>
    </aside>
  );
};

export default Sidebar; 